import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

// In-page stand-in for the browser's "leave site?" prompt, for in-app
// navigation (react-router v5 history.block). A browser tab close or reload
// is covered by beforeunload, whose message the browser writes itself.
// title / message / leaveLabel / label (the dialog's accessible name) name what would be lost (defaults: a connection's
// unsaved changes).
//
// A transition that keeps the same pathname is the app's own doing, never
// the person leaving: a post-save redirect that lands back on this exact
// page, or another part of the page syncing its own state (a grid's sort/
// search/scroll position) into the query string. Those fire on their own
// schedule -- a debounced write can land well after `when` turns true and
// the guard arms -- so only a transition that actually changes the page is
// held for confirmation. (A caller that calls its own post-save redirect
// synchronously, before `when` turns true, never reaches this check at
// all: the block isn't registered yet.)
export const LeaveGuard = ({ when, title = 'You have unsaved changes', message = 'Leave this connection without saving them?', leaveLabel = 'Leave without saving', label = 'unsaved changes' }) => {
  const history = useHistory()
  const [pending, setPending] = useState(null)
  const unblock = useRef(null)

  useEffect(() => {
    if (!when) return undefined
    unblock.current = history.block(location => {
      if (location.pathname === history.location.pathname) return true
      setPending(location)
      return false
    })
    const onBeforeUnload = e => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => { unblock.current && unblock.current(); window.removeEventListener('beforeunload', onBeforeUnload) }
  }, [when, history])

  // Escape is Stay: the safe choice, and the one that keeps the edits.
  useEffect(() => {
    if (!pending) return undefined
    const onKey = e => { if (e.key === 'Escape') setPending(null) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [pending])

  if (!pending) return null

  const leave = () => { unblock.current && unblock.current(); const to = pending; setPending(null); history.push(to) }

  return (
    <div className="sw-drawer justify-content-center align-items-center">
      <div className="bg-body rounded-3 p-4 shadow" role="dialog" aria-modal="true" aria-label={label} style={{ maxWidth: 420 }}>
        <h2 className="h5">{title}</h2>
        <p className="text-body-secondary">{message}</p>
        <div className="d-flex gap-2 justify-content-end">
          <button type="button" className="btn btn-primary" autoFocus onClick={() => setPending(null)}>Stay</button>
          <button type="button" className="btn btn-outline-danger" onClick={leave}>{leaveLabel}</button>
        </div>
      </div>
    </div>
  )
}
